import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Paper,
  Tooltip,
  useTheme,
  Chip,
  alpha,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import HistoryIcon from "@mui/icons-material/History";
import ForkRightIcon from "@mui/icons-material/ForkRight";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/authSlice";

const SnippetModal = ({
  open,
  handleClose,
  originalCode,
  title,
  language,
  additionalInfo = "",
  snippetId,
  isForked = false, // Determines if the modal is read-only
}) => {
  const theme = useTheme();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(originalCode);
    setSnackbarMessage("Snippet copied to clipboard!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const forkCode = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/snippets/fork-snippet`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ originalSnippetId: snippetId }),
        }
      );

      const data = await response.json();

      if (data.message === "Invalid token") {
        // Remove user from local storage
        dispatch(logout());
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fork snippet");
      }

      setSnackbarMessage(`Snippet forked successfully: ${data.snippet.title}`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error forking snippet:", error);
      setSnackbarMessage("Failed to fork snippet. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isForked && (
            <HistoryIcon sx={{ color: theme.palette.secondary.main }} />
          )}
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <Chip
            label={language}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          />
          {isForked && (
            <Chip
              label="Forked Snippet"
              size="small"
              color="primary"
              sx={{ fontWeight: "bold" }}
            />
          )}
          {!isForked && (
            <Chip
              label="Original Snippet"
              size="small"
              color="secondary"
              sx={{ fontWeight: "bold" }}
            />
          )}
        </Box>
        <IconButton aria-label="close" onClick={handleClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {additionalInfo && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 3,
            py: 1,
            bgcolor: alpha(theme.palette.info.main, 0.1),
          }}
        >
          <InfoIcon fontSize="small" sx={{ mr: 1, color: "info.main" }} />
          <Typography variant="body2" color="text.secondary">
            {additionalInfo}
          </Typography>
        </Box>
      )}

      <DialogContent dividers sx={{ p: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            position: "relative",
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha("#000", 0.7)
                : alpha("#f5f5f5", 0.8),
            p: 2,
            borderRadius: 1,
            maxHeight: "60vh",
            overflow: "auto",
            ...(isForked && {
              borderColor: theme.palette.secondary.main,
              boxShadow: `0 0 0 1px ${alpha(
                theme.palette.secondary.main,
                0.3
              )}`,
            }),
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <Tooltip title="Copy Code">
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Fork Code">
              <IconButton size="small" onClick={forkCode}>
                <ForkRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              fontFamily: "monospace",
              fontSize: "0.9rem",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: theme.palette.text.primary,
            }}
          >
            {originalCode}
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SnippetModal;
